import { calculateScale } from "../../FloorPlanModal/utils/viewport";
import { IDENTITY_VIEW, MIN_ZOOM, clampZoom } from "./viewTransform";

// Off-site people and machines (D-18/D-18a, D-29): a GPS fix outside the
// site rectangle is kept and flagged `offSite`. A plane canvas always draws
// it, around the site, map on or off. Only a line/tunnel strip, which has no
// space beside the site (it would snap the dot onto the strip's end), hides
// it. The card title counts it either way. A missing flag is on site.

/** { shown, hidden }: `shown` is the input array itself when nothing hides. */
export const splitOffSite = (items, drawable) => {
  const list = items || [];
  if (drawable) return { shown: list, hidden: 0 };
  const shown = list.filter((item) => !item.offSite);
  return shown.length === list.length
    ? { shown: list, hidden: 0 }
    : { shown, hidden: list.length - shown.length };
};

const countFlagged = (items) =>
  (items || []).filter((item) => item.offSite).length;

export const countOffSite = (personnel, equipment) => ({
  people: countFlagged(personnel),
  machines: countFlagged(equipment),
});

export const offSiteLabel = (people, machines) =>
  `場外：${people} 人、${machines} 機具`;

// Room kept free along every canvas edge by the fit, in px: the zoom pill and
// the 圖層 trigger sit in the top corners, and a marker is ~20 px across.
export const FIT_MARGIN_PX = 40;

// The metre points an off-site item is drawn at: a person's position, a
// machine's body points.
const pointsOf = (item) => {
  if (!item?.offSite) return [];
  if (item.position_meters) return [item.position_meters];
  return item.pose?.bodyPoints || [];
};

// The pan along one axis: the bbox centre (`centrePx` at pan 0) in the middle
// of the canvas, then held so the site (`sitePx` long, starting at pan) stays
// inside the margins. At the zoom cap the bbox may not fit, and the site is
// what the operator must not lose.
const panAxis = (centrePx, sitePx, canvasPx) => {
  const lo = FIT_MARGIN_PX;
  const hi = Math.max(lo, canvasPx - FIT_MARGIN_PX - sitePx);
  return Math.min(Math.max(canvasPx / 2 - centrePx, lo), hi);
};

/**
 * The view that just fits the site and every off-site item on a plane
 * canvas: zoomed out no further than needed, never in past fit, never below
 * MIN_ZOOM_GEO (an item beyond that stays counted, not reachable). The
 * identity view when there is nothing off site or no scale yet.
 */
export const fitOffSiteView = ({
  items,
  dimensions,
  canvasSize,
  layoutType,
}) => {
  const points = (items || []).flatMap(pointsOf);
  const { scale, isValid } = calculateScale(dimensions, canvasSize, layoutType);
  if (!isValid || points.length === 0) return IDENTITY_VIEW;

  const { width, height } = dimensions;
  const xs = [0, width, ...points.map((p) => p.x)];
  const ys = [0, height, ...points.map((p) => p.y)];
  const [minX, maxX] = [Math.min(...xs), Math.max(...xs)];
  const [minY, maxY] = [Math.min(...ys), Math.max(...ys)];

  const roomW = Math.max(canvasSize.width - 2 * FIT_MARGIN_PX, 1);
  const roomH = Math.max(canvasSize.height - 2 * FIT_MARGIN_PX, 1);
  const zoom = clampZoom(
    Math.min(
      MIN_ZOOM,
      roomW / ((maxX - minX) * scale),
      roomH / ((maxY - minY) * scale),
    ),
  );

  // y-up: metre y sits (height - y) * s px below the floor box's top.
  const s = scale * zoom;
  return {
    zoom,
    pan: {
      x: panAxis(((minX + maxX) / 2) * s, width * s, canvasSize.width),
      y: panAxis(
        (height - (minY + maxY) / 2) * s,
        height * s,
        canvasSize.height,
      ),
    },
  };
};
